import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { BASE_URL } from '../../core/global/constants';
import { Read, ReadResult } from '../models/read';
import { EntityType } from '../models/entity-type.enum';

import {
    AvionBusinessEntity,
    ExecuteQueryResult,
    Individual,
    ListResult, SystemCheckSumsResult,
    SysUser,
    Usr,
} from '../models/common';
import { Save, SaveList, SaveListResult, SaveResult } from '../models/save';
import { UserService } from '../../core/services/user.service';

@Injectable({
    providedIn: 'root',
})
export class CommonApiService {
    private baseUrlProvider = inject<Promise<string>>(BASE_URL);
    private http = inject(HttpClient);
    private userService = inject(UserService);

    public readIndividual(data: Read): Observable<ReadResult<Individual>> {
        return from(this.baseUrlProvider).pipe(
            switchMap(baseUrl =>
                this.http.post(baseUrl + 'Read', data) as Observable<ReadResult<Individual>>
            )
        );
    }

    public read<T>(entityId: string, entityType: EntityType, displayId?: number): Observable<ReadResult<T>> {
        const usr = this.getUsr('read');
        const requestData: Read = {} as Read;
        requestData.abe = {
            EntityId: entityId,
            EntityTypeId: entityType,
        };
        if (displayId) {
            requestData.abe.DisplayId = displayId;
        }
        requestData.usr = usr;
        return from(this.baseUrlProvider).pipe(
            switchMap(baseUrl =>
                this.http.post(baseUrl + 'Read', requestData) as Observable<ReadResult<T>>
            )
        )
    }


    public executeQuery<T>(entityType: EntityType, queryArgs: any) {
        const usr = this.getUsr('save');
        const requestData = {
            queryEntityId: entityType,
            queryArgs,
            usr,
        }

        return from(this.baseUrlProvider).pipe(
            switchMap(baseUrl =>
                this.http.post(baseUrl + 'ExecuteQuery', requestData) as Observable<ExecuteQueryResult<T>>
            )
        );
    }

    public readAll<T>(abe: AvionBusinessEntity, filters?: any): Observable<ListResult<T>> {
        const usr = this.getUsr('read');
        const requestData = {
            abe,
            usr: usr
        } as any;

        if (filters) {
            requestData.filters = filters;
        }

        return from(this.baseUrlProvider).pipe(
            switchMap(baseUrl =>
                this.http.post(baseUrl + 'ReadAll', requestData) as Observable<ListResult<T>>
            )
        );
    }

    public listByOwner<T>(entityType: EntityType, entityId: string, entityTypeId: string): Observable<ListResult<T>> {
        const usr = this.getUsr('list');
        const data = {
            entityTypeId: entityType,
            ownerEntityId: entityId,
            ownerEntityTypeId: entityTypeId,
            usr
        }

        return from(this.baseUrlProvider).pipe(
            switchMap(baseUrl =>
                this.http.post(baseUrl + 'ListByOwner', data) as Observable<ListResult<T>>
            )
        );
    }

    public getSurveyQuestions<T>(params: any): Observable<ListResult<T>> {
        const usr = this.getUsr('list');
        const data = {
            entityTypeId: params.entityType,
            ownerEntityId: params.ownerEntityId,
            ownerEntityTypeId: params.ownerEntityTypeId,
            usr
        } as any;

        if (params.filters) {
            data.filters = params.filters;
        }

        if (params.sortOptions) {
            data.sortOptions = params.sortOptions;
        }

        if (params.pageSize) {
            data.pageSize = params.pageSize;
        }

        if (params.pageNum) {
            data.pageNum = params.pageNum;
        }

        return from(this.baseUrlProvider).pipe(
            switchMap(baseUrl =>
                this.http.post(baseUrl + 'ListByOwner', data) as Observable<ListResult<T>>
            )
        );
    }

    public save<T>(item: T) {
        const usr: SysUser = this.userService.getUser() || {} as SysUser;
        const abe: T = {...item}

        const requestData: Save<T> = {
            abe: abe,
            usr
        };

        return from(this.baseUrlProvider).pipe(
            switchMap(baseUrl =>
                this.http.post(baseUrl + 'Save', requestData) as Observable<SaveResult<T>>
            )
        );
    }

    public saveList<T>(items: T[]): Observable<SaveListResult<T>> {
        const usr: SysUser = this.userService.getUser() || {} as SysUser;
        const requestData: SaveList<T> = {
            abes: items,
            usr
        };

        return from(this.baseUrlProvider).pipe(
            switchMap(baseUrl =>
                this.http.post(baseUrl + 'SaveList', requestData) as Observable<SaveListResult<T>>
            )
        );
    }

    private getUsr(action: 'read' | 'save' | 'list' | 'delete'): Usr {
        const user = this.userService.getUser() || {};
        const usr: Usr = {
            UserName: user.UserName || '',
            EntityId: user.EntityId || '',
            EntityTypeId: user.EntityTypeId || '',
            SessionId: user?.SessionId || '',
        };
        if (action === 'save') {
            usr.Description = user.Description;
            usr.DisplayId = user.DisplayId;
            usr.__type = user.__type;
        }
        return usr;
    }

    public getSystemCheckSums(): Observable<SystemCheckSumsResult> {
        const user = this.userService.getUser() || {};

        return from(this.baseUrlProvider).pipe(
            switchMap(baseUrl =>
                this.http.post(baseUrl + 'GetSystemChecksums', {
                    usr: user,
                }) as Observable<SystemCheckSumsResult>
            )
        );
    }
}
